/* ============================================================
   HERCULES — LP Buybox v3 ("Concept C")
   Drives Skio via its public API, submits via Skio's existing
   product form (no competing form).

   v3 adds: metafield-driven tier ladder, quantity stepper as the
   primary control, unlock progress bar + nudge, a prominent hero
   (regular strike-through / sale / "XX% off"), and a live "% off"
   pill on the sticky add-to-cart bars.
   ============================================================ */

class LPBuybox extends HTMLElement {
  constructor() {
    super();
    this.basePrice = parseInt(this.dataset.basePrice, 10) || 0;
    // Subscribe discount: read from data attr, fallback to 10% (the business rule).
    this.subDiscount = parseInt(this.dataset.subDiscount, 10) || 10;
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.productFormId = this.dataset.productFormId;

    // Map of selling_plan_id -> unit price in cents. Authoritative source for
    // subscribe pricing - mirrors what Shopify charges in the cart.
    this.subAllocations = {};
    try {
      this.subAllocations = JSON.parse(this.dataset.subAllocations || '{}');
    } catch (e) {
      console.warn('[lp-buybox] could not parse sub-allocations', e);
    }

    // Concept C - metafield-driven tier ladder + quantity range
    this.maxTier = parseInt(this.dataset.maxTier, 10) || 1;   // top breakpoint (unlock bar)
    this.maxQty = parseInt(this.dataset.maxQty, 10) || 99;     // stepper hard ceiling
    this.tiers = [];
    try {
      this.tiers = JSON.parse(this.dataset.tiers || '[]')
        .sort((a, b) => a.quantity - b.quantity);
    } catch (e) {
      console.warn('[lp-buybox] could not parse tiers', e);
    }
    this.stackTierOnSubscribe = this.dataset.stackTierSub !== 'false';

    this.state = {
      variantId: null,
      option: 'subscribe',
      qty: 1,
      sellingPlanId: null
    };

    // Flag set briefly when a purchase option just changed, so bubbled
    // qty-button clicks in the same event tick don't reset state.qty.
    this._optionJustChanged = false;

    this.elements = {};
    this.skio = null;
    this.skioReadyTimer = null;
  }

  connectedCallback() {
    try {
      this.cacheElements();
      this.initVariantState();
      this.initSubscribeState();
      this.bindEvents();
      this.waitForSkio();
      this.render();
    } catch (err) {
      console.error('[lp-buybox] connectedCallback ERROR:', err);
      const errDiv = document.createElement('div');
      errDiv.style.cssText = 'background:#fee;color:#a00;padding:10px;border:2px solid #a00;margin:10px 0;font-size:12px;font-family:monospace;white-space:pre-wrap;';
      errDiv.textContent = 'LP-BUYBOX ERROR: ' + err.message + '\n\n' + err.stack;
      this.prepend(errDiv);
    }
  }

  cacheElements() {
    this.elements.chips = this.querySelectorAll('[data-variant-chip]');
    this.elements.purchaseRadios = this.querySelectorAll('[data-purchase-radio]');
    this.elements.qtyButtons = this.querySelectorAll('[data-qty-btn]');
    this.elements.freqButtons = this.querySelectorAll('[data-freq-btn]');
    this.elements.qtyStepperVal = this.querySelector('[data-qty-stepper-val]');
    this.elements.qtyStepperBtns = this.querySelectorAll('[data-qty-step]');
    this.elements.atc = this.querySelector('[data-lp-atc]');
    this.elements.atcPrice = this.querySelector('[data-atc-price]');
    this.elements.onetimePrice = this.querySelector('[data-onetime-price]');
    this.elements.onetimeSub = this.querySelector('[data-onetime-sub]');
    this.elements.subscribePrice = this.querySelector('[data-subscribe-price]');
    this.elements.subscribeCompare = this.querySelector('[data-subscribe-compare]');
    this.elements.saveLineOnetime = this.querySelector('[data-save-line="onetime"]');
    this.elements.saveLineSubscribe = this.querySelector('[data-save-line="subscribe"]');
    this.elements.options = this.querySelectorAll('.lp-buybox__option');

    // Concept C elements
    this.elements.qtyUnit  = this.querySelector('[data-qty-unit]');
    this.elements.fill     = this.querySelector('[data-tbb-fill]');
    this.elements.nudge    = this.querySelector('[data-tbb-nudge]');
    this.elements.heroSale = this.querySelector('[data-hero-sale]');
    this.elements.heroReg  = this.querySelector('[data-hero-reg]');
    this.elements.heroOff  = this.querySelector('[data-hero-off]');
    this.elements.heroSave = this.querySelector('[data-hero-save]');
  }

  initVariantState() {
    const selectedChip = this.querySelector('[data-variant-chip].is-selected');
    if (selectedChip && selectedChip.dataset.variantId) {
      this.state.variantId = parseInt(selectedChip.dataset.variantId, 10);
      return;
    }
    // Fallback for products with no flavour chips: the default variant id is
    // on the <lp-buybox> element itself. This keeps Add to Cart working.
    if (this.dataset.variantId) {
      this.state.variantId = parseInt(this.dataset.variantId, 10);
      return;
    }
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product && window.ShopifyAnalytics.meta.product.variants && window.ShopifyAnalytics.meta.product.variants[0]) {
      this.state.variantId = window.ShopifyAnalytics.meta.product.variants[0].id;
    }
  }

  initSubscribeState() {
    const firstFreq = this.querySelector('[data-freq-btn]');
    if (firstFreq) {
      this.state.sellingPlanId = parseInt(firstFreq.dataset.planId, 10);
    }
    // If there's no subscribe card (no Skio), default to one-time.
    if (!this.elements.subscribePrice) {
      this.state.option = 'onetime';
    }
  }

  bindEvents() {
    this.elements.chips.forEach(chip => {
      chip.addEventListener('click', () => this.onVariantChip(chip));
    });

    this.elements.options.forEach(option => {
      const card = option.querySelector('.lp-buybox__option-card');
      if (!card) return;
      card.addEventListener('click', (e) => {
        if (e.target.closest('[data-qty-btn]') || e.target.closest('[data-freq-btn]')) return;
        const value = option.dataset.option;
        if (this.state.option === value) return;
        const radio = option.querySelector('[data-purchase-radio]');
        if (radio) radio.checked = true;
        this.onPurchaseOption(value);
      });
    });

    this.elements.qtyButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const scope = btn.dataset.qtyBtn;
        if (scope !== this.state.option) return;
        if (this._optionJustChanged) return;
        this.onQtyChange(btn);
      });
    });

    this.elements.freqButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.onFreqChange(btn);
      });
    });

    this.elements.qtyStepperBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const delta = parseInt(btn.dataset.qtyStep, 10);
        this.onStepperChange(delta);
      });
    });

    // Concept C - unlock nudge jumps to the next tier that raises the discount
    if (this.elements.nudge) {
      this.elements.nudge.addEventListener('click', () => {
        const curDisc = this.getTierDiscountForQty(this.state.option, this.state.qty);
        const next = this.tiers.find(t => t.quantity > this.state.qty && (t.discount || 0) > curDisc);
        if (!next) return;
        this.state.qty = next.quantity;
        this.updateStepper();
        this.syncQtyButtonsToStepper();
        this.render();
      });
    }

    if (this.elements.atc) {
      this.elements.atc.addEventListener('click', (e) => {
        e.preventDefault();
        this.addToCart();
      });
    }

    document.querySelectorAll('[data-lp-atc-sticky]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.addToCart();
      });
    });

    document.querySelectorAll('[data-sticky-choose-options]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const target = document.querySelector('lp-buybox');
        if (!target) return;
        const headerOffset = 20;
        const targetTop = target.getBoundingClientRect().top + window.pageYOffset - headerOffset;
        window.scrollTo({ top: targetTop, behavior: 'smooth' });
      });
    });

    this.querySelectorAll('[data-sticky-purchase-btn]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const value = btn.dataset.stickyPurchaseBtn;
        if (this.state.option === value) return;
        this.onPurchaseOption(value);
      });
    });

    this.querySelectorAll('[data-sticky-qty-step]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const delta = parseInt(btn.dataset.stickyQtyStep, 10);
        this.onStepperChange(delta);
      });
    });

    this.querySelectorAll('[data-sticky-flavour-trigger]').forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const wrap = trigger.closest('[data-sticky-flavour]');
        if (!wrap) return;
        const isOpen = wrap.dataset.open === 'true';
        document.querySelectorAll('[data-sticky-flavour][data-open="true"]').forEach(w => {
          if (w !== wrap) {
            w.dataset.open = 'false';
            const t = w.querySelector('[data-sticky-flavour-trigger]');
            if (t) t.setAttribute('aria-expanded', 'false');
          }
        });
        wrap.dataset.open = isOpen ? 'false' : 'true';
        trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      });
    });

    this.querySelectorAll('[data-sticky-flavour] [data-variant-chip]').forEach(chip => {
      chip.addEventListener('click', () => {
        const wrap = chip.closest('[data-sticky-flavour]');
        if (wrap) {
          wrap.dataset.open = 'false';
          const t = wrap.querySelector('[data-sticky-flavour-trigger]');
          if (t) t.setAttribute('aria-expanded', 'false');
        }
      });
    });

    document.addEventListener('click', (e) => {
      const openDropdowns = document.querySelectorAll('[data-sticky-flavour][data-open="true"]');
      openDropdowns.forEach(wrap => {
        if (!wrap.contains(e.target)) {
          wrap.dataset.open = 'false';
          const t = wrap.querySelector('[data-sticky-flavour-trigger]');
          if (t) t.setAttribute('aria-expanded', 'false');
        }
      });
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      document.querySelectorAll('[data-sticky-flavour][data-open="true"]').forEach(wrap => {
        wrap.dataset.open = 'false';
        const t = wrap.querySelector('[data-sticky-flavour-trigger]');
        if (t) t.setAttribute('aria-expanded', 'false');
      });
    });
  }

  waitForSkio() {
    let attempts = 0;
    const maxAttempts = 40;
    const check = () => {
      const skioEl = document.querySelector('skio-plan-picker');
      if (skioEl && typeof skioEl.selectSellingPlanGroup === 'function') {
        this.skio = skioEl;
        this.syncSkio();
        return;
      }
      attempts += 1;
      if (attempts < maxAttempts) {
        this.skioReadyTimer = setTimeout(check, 150);
      }
    };
    check();
  }

  onVariantChip(chip) {
    if (chip.classList.contains('is-unavailable')) return;
    const optionIndex = parseInt(chip.dataset.optionIndex, 10);
    const variantId = chip.dataset.variantId ? parseInt(chip.dataset.variantId, 10) : null;

    const siblings = document.querySelectorAll(`[data-variant-chip][data-option-index="${optionIndex}"]`);
    siblings.forEach(s => {
      if (s.dataset.optionValue === chip.dataset.optionValue) {
        s.classList.add('is-selected');
        s.setAttribute('aria-checked', 'true');
      } else {
        s.classList.remove('is-selected');
        s.setAttribute('aria-checked', 'false');
      }
    });

    if (variantId) {
      this.state.variantId = variantId;
    }
    this.syncSkio();
    this.render();
  }

  onPurchaseOption(value) {
    this.state.option = value;
    this.elements.options.forEach(option => {
      const isSelected = option.dataset.option === value;
      option.classList.toggle('is-selected', isSelected);
    });
    this._optionJustChanged = true;
    setTimeout(() => { this._optionJustChanged = false; }, 50);
    this.syncQtyButtonsToStepper();
    this.syncSkio();
    this.render();
  }

  getSelectedQtyInScope(scope) {
    const selected = this.querySelector(`[data-qty-btn="${scope}"].is-selected`);
    if (selected) return parseInt(selected.dataset.qty, 10);
    return 1;
  }

  onQtyChange(btn) {
    const qty = parseInt(btn.dataset.qty, 10);
    this.state.qty = qty;
    this.updateStepper();
    this.syncQtyButtonsToStepper();
    this.render();
  }

  onStepperChange(delta) {
    let newQty = this.state.qty + delta;
    if (newQty < 1) newQty = 1;
    if (newQty > this.maxQty) newQty = this.maxQty;
    this.state.qty = newQty;
    this.updateStepper();
    this.syncQtyButtonsToStepper();
    this.render();
  }

  // Stepper changed -> reflect in any 1/2/3 buttons (legacy; no-ops if absent)
  syncQtyButtonsToStepper() {
    const qty = this.state.qty;
    ['onetime', 'subscribe'].forEach(scope => {
      const buttons = this.querySelectorAll(`[data-qty-btn="${scope}"]`);
      if (buttons.length === 0) return;

      let exactBtn = null;
      let fallbackBtn = null;
      let fallbackQty = 0;
      buttons.forEach(b => {
        const bq = parseInt(b.dataset.qty, 10);
        if (bq === qty) {
          exactBtn = b;
        } else if (bq < qty && bq > fallbackQty) {
          fallbackQty = bq;
          fallbackBtn = b;
        }
      });
      const winner = exactBtn || fallbackBtn;
      buttons.forEach(b => {
        if (b === winner) {
          b.classList.add('is-selected');
          b.setAttribute('aria-checked', 'true');
        } else {
          b.classList.remove('is-selected');
          b.setAttribute('aria-checked', 'false');
        }
      });
    });
  }

  updateStepper() {
    if (this.elements.qtyStepperVal) {
      this.elements.qtyStepperVal.textContent = this.state.qty;
    }
  }

  onFreqChange(btn) {
    const planId = parseInt(btn.dataset.planId, 10);
    const siblings = this.querySelectorAll('[data-freq-btn]');
    siblings.forEach(s => {
      s.classList.remove('is-selected');
      s.setAttribute('aria-checked', 'false');
    });
    btn.classList.add('is-selected');
    btn.setAttribute('aria-checked', 'true');

    this.state.sellingPlanId = planId;
    this.syncSkio();
    this.render();
  }

  /* ============================================================
     PRICE MATH
     ============================================================ */
  // Smallest quantity at which the maximum discount applies (the real cap).
  // Used for the unlock bar fill and to bound the nudge, so filler tiers that
  // repeat the top discount (e.g. 6-10 all 29%) don't read as "more to unlock".
  topTierQty() {
    if (!this.tiers.length) return this.maxTier || 1;
    const max = Math.max.apply(null, this.tiers.map(t => t.discount || 0));
    const qs = this.tiers.filter(t => (t.discount || 0) === max).map(t => t.quantity);
    return qs.length ? Math.min.apply(null, qs) : (this.maxTier || 1);
  }

  // Tier discount by quantity - reads the metafield-driven ladder.
  // `scope` is kept for signature compatibility; both options share the ladder.
  getTierDiscountForQty(scope, qty) {
    let best = 0;
    this.tiers.forEach(t => { if (t.quantity <= qty) best = t.discount || 0; });
    return best;
  }

  calculatePrices() {
    const onetimeQty = this.state.qty;
    const subscribeQty = this.state.qty;

    const onetimeTierDiscount = this.getTierDiscountForQty('onetime', onetimeQty);
    const subscribeTierDiscount = this.getTierDiscountForQty('subscribe', subscribeQty);

    // Shopify rounds UP (Math.ceil) when applying % discounts to prices internally.
    // We must mirror that here so displayed prices match the cart exactly.
    //
    // Formula proven against live cart values (Burn of the Gods, base 8495c):
    //   onetime_unit = ceil(base * (100 - tierDiscount) / 100)
    //   sub_unit     = ceil(onetime_unit * (100 - subDiscount) / 100)
    //
    // This means subscribe is always derived from the tier-adjusted onetime unit,
    // NOT from the subAllocations map (which is qty=1 only and can't reflect tiers).
    // The alloc map is still used for the option-card display price at qty=1 only.
    const onetimeUnit = Math.ceil(this.basePrice * (100 - onetimeTierDiscount) / 100);
    const onetimeTotal = onetimeUnit * onetimeQty;
    const onetimeOriginal = this.basePrice * onetimeQty;
    const onetimeSavings = onetimeOriginal - onetimeTotal;

    // Subscribe: apply sub% on top of the tier-adjusted onetime unit (ceil throughout).
    const subTierUnit = Math.ceil(onetimeUnit * (100 - this.subDiscount) / 100);
    const subTotal = subTierUnit * subscribeQty;
    const subOriginal = this.basePrice * subscribeQty;
    const subSavings = subOriginal - subTotal;

    return {
      onetime: {
        unit: onetimeUnit,
        total: onetimeTotal,
        original: onetimeOriginal,
        savings: onetimeSavings,
        qty: onetimeQty,
        tierDiscount: onetimeTierDiscount
      },
      subscribe: {
        unit: subTierUnit,
        total: subTotal,
        original: subOriginal,
        savings: subSavings,
        qty: subscribeQty,
        tierDiscount: subscribeTierDiscount
      }
    };
  }

  formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const format = this.moneyFormat || '${{amount}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;

    function withDelimiters(number, precision, thousands, decimal) {
      precision = (precision == null) ? 2 : precision;
      thousands = (thousands == null) ? ',' : thousands;
      decimal = (decimal == null) ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const frac = parts[1] ? (decimal + parts[1]) : '';
      return whole + frac;
    }

    const match = format.match(placeholder);
    if (!match) return format;
    let value = '';
    switch (match[1]) {
      case 'amount':
        value = withDelimiters(cents, 2, ',', '.'); break;
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', ''); break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.'); break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }
    return format.replace(placeholder, value);
  }

  /* ============================================================
     RENDER
     ============================================================ */
  render() {
    const prices = this.calculatePrices();

    if (this.elements.onetimePrice) {
      this.elements.onetimePrice.textContent = this.formatMoney(prices.onetime.total);
    }
    if (this.elements.onetimeSub) {
      this.elements.onetimeSub.textContent = 'Pay once · no commitment';
    }

    if (this.elements.subscribePrice) {
      this.elements.subscribePrice.textContent = this.formatMoney(prices.subscribe.total);
    }
    if (this.elements.subscribeCompare) {
      if (prices.subscribe.savings > 0) {
        this.elements.subscribeCompare.textContent = this.formatMoney(prices.subscribe.original);
      } else {
        this.elements.subscribeCompare.textContent = '';
      }
    }

    const savedPillOnetime = this.querySelector('[data-saved-pill="onetime"]');
    if (savedPillOnetime) {
      if (prices.onetime.savings > 0) {
        savedPillOnetime.textContent = `${this.formatMoney(prices.onetime.savings)} saved`;
        savedPillOnetime.hidden = false;
      } else {
        savedPillOnetime.hidden = true;
      }
    }

    const savedPillSubscribe = this.querySelector('[data-saved-pill="subscribe"]');
    if (savedPillSubscribe) {
      if (prices.subscribe.savings > 0) {
        savedPillSubscribe.textContent = `${this.formatMoney(prices.subscribe.savings)} saved`;
        savedPillSubscribe.hidden = false;
      } else {
        savedPillSubscribe.hidden = true;
      }
    }

    const activePrice = this.state.option === 'subscribe' && this.elements.subscribePrice
      ? prices.subscribe.total
      : prices.onetime.total;
    if (this.elements.atcPrice) {
      this.elements.atcPrice.textContent = this.formatMoney(activePrice);
    }
    document.querySelectorAll('[data-sticky-price]').forEach(el => {
      el.textContent = this.formatMoney(activePrice);
    });
    document.querySelectorAll('[data-sticky-atc-price]').forEach(el => {
      el.textContent = this.formatMoney(activePrice);
    });

    const baseTotal = this.basePrice * this.state.qty;
    const hasDiscount = activePrice < baseTotal;
    document.querySelectorAll('[data-sticky-compare]').forEach(el => {
      if (hasDiscount) {
        el.textContent = this.formatMoney(baseTotal);
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    });

    const selectedChipForLabel = this.querySelector('[data-variant-chip].is-selected');
    if (selectedChipForLabel) {
      const variantLabel = selectedChipForLabel.dataset.optionValue || selectedChipForLabel.textContent.trim();
      document.querySelectorAll('[data-sticky-variant-current]').forEach(el => {
        el.textContent = variantLabel;
      });
    }

    document.querySelectorAll('[data-sticky-purchase-btn]').forEach(btn => {
      btn.classList.toggle('is-active', btn.dataset.stickyPurchaseBtn === this.state.option);
    });

    document.querySelectorAll('[data-sticky-qty-val]').forEach(el => {
      el.textContent = this.state.qty;
    });

    const selectedChip = this.querySelector('[data-variant-chip].is-selected');
    const isUnavailable = selectedChip ? selectedChip.classList.contains('is-unavailable') : false;
    if (this.elements.atc) {
      const labelEl = this.elements.atc.querySelector('.lp-buybox__atc-label');
      const sepEl = this.elements.atc.querySelector('.lp-buybox__atc-sep');
      const priceEl = this.elements.atc.querySelector('.lp-buybox__atc-price');
      this.elements.atc.classList.toggle('is-unavailable', isUnavailable);
      this.elements.atc.disabled = isUnavailable;
      if (labelEl) labelEl.textContent = isUnavailable ? 'Out of stock' : 'Add to cart';
      if (sepEl) sepEl.hidden = isUnavailable;
      if (priceEl) priceEl.hidden = isUnavailable;
    }
    document.querySelectorAll('[data-lp-atc-sticky]').forEach(stickyAtc => {
      stickyAtc.classList.toggle('is-unavailable', isUnavailable);
      stickyAtc.disabled = isUnavailable;
      const stickyLabel = stickyAtc.querySelector('.lp-buybox-sticky__atc-label');
      const stickyAtcPrice = stickyAtc.querySelector('[data-sticky-atc-price]');
      if (stickyLabel) {
        stickyLabel.textContent = isUnavailable ? 'Out of stock' : 'Add to cart';
        if (stickyAtcPrice) stickyAtcPrice.hidden = isUnavailable;
      } else {
        stickyAtc.textContent = isUnavailable ? 'Out of stock' : 'Add to cart';
      }
    });

    const afterpayEl = this.querySelector('[data-lp-afterpay-amount]');
    if (afterpayEl) {
      const perPayment = Math.round(activePrice / 4);
      afterpayEl.textContent = this.formatMoney(perPayment);
    }

    // Concept C - hero, unlock bar, sticky % off
    this.renderConceptC(prices);

    /* Broadcast buy-box state so decoupled components can react without
       reaching into this class. Consumed by the GWP rail (lp-gwp-rail) to
       lock/unlock gift tiles live as quantity and purchase option change.
       render() is the single update path, so this fires on every change. */
    document.dispatchEvent(new CustomEvent('lp-buybox:state', {
      detail: {
        qty: this.state.qty,
        option: this.state.option,
        sellingPlanId: this.state.sellingPlanId,
        variantId: this.state.variantId
      }
    }));
  }

  /* ============================================================
     CONCEPT C RENDER - hero / unlock / sticky % off
     ============================================================ */
  renderConceptC(prices) {
    const qty = this.state.qty;
    const active = (this.state.option === 'subscribe' && this.elements.subscribePrice)
      ? prices.subscribe
      : prices.onetime;

    const rrpTotal  = this.basePrice * qty;
    const saleTotal = active.total;

   // % off pill: additive sum of tier% + sub% when subscribe is active
    // (e.g. tier 9% + sub 10% = "19% off"). When one-time is active, show tier% vs RRP.
    const isSubscribeActive = this.state.option === 'subscribe' && this.elements.subscribePrice;
    const regTotal = isSubscribeActive ? prices.onetime.total : rrpTotal;
    let off;
    if (isSubscribeActive) {
      off = (prices.subscribe.tierDiscount || 0) + this.subDiscount;
    } else {
      off = rrpTotal > 0 ? Math.round((1 - saleTotal / rrpTotal) * 100) : 0;
    }

    if (this.elements.heroSale) this.elements.heroSale.textContent = this.formatMoney(saleTotal);
    if (this.elements.heroReg) {
      this.elements.heroReg.textContent = this.formatMoney(regTotal);
      this.elements.heroReg.hidden = saleTotal >= regTotal;
    }
    if (this.elements.heroOff) {
      this.elements.heroOff.hidden = off <= 0;
      if (off > 0) this.elements.heroOff.textContent = off + '% off';
    }
    if (this.elements.heroSave) {
      // "You save" stays vs RRP so the full saving (sale + sub) is shown.
      const saved = rrpTotal - saleTotal;
      this.elements.heroSave.hidden = saved <= 0;
      if (saved > 0) this.elements.heroSave.textContent = 'You save ' + this.formatMoney(saved);
    }
    if (this.elements.qtyUnit) this.elements.qtyUnit.textContent = qty === 1 ? 'tub' : 'tubs';

    // Unlock progress bar — fills 1 → the smallest quantity at the top discount,
    // stays full beyond it. Uses the ladder (not maxTier) so filler tiers up to
    // 10 don't stretch the bar past where the discount actually caps.
    if (this.elements.fill) {
      const top = this.topTierQty();
      const span = top - 1;
      const filledQty = Math.min(qty, top);
      this.elements.fill.style.width = (span <= 0 ? 100 : ((filledQty - 1) / span) * 100) + '%';
    }
  if (this.elements.nudge) {
      // Only treat a higher quantity as a new unlock if it actually raises the
      // discount — otherwise filler tiers (6-10 all the same %) read as "add more".
      const curDisc = this.getTierDiscountForQty(this.state.option, qty);
      const next = this.tiers.find(t => t.quantity > qty && (t.discount || 0) > curDisc);
      if (next) {
        const need = next.quantity - qty;
        this.elements.nudge.textContent =
          'Add ' + need + ' more ' + (need === 1 ? 'tub' : 'tubs') +
          ' to unlock ' + next.discount + '% off →';
        this.elements.nudge.classList.remove('is-maxed');
      } else if (!isSubscribeActive && this.elements.subscribePrice) {
        // Top quantity tier reached on one-time — subscribing still adds another 10%.
        this.elements.nudge.textContent =
          'Subscribe to save another ' + this.subDiscount + '%';
        this.elements.nudge.classList.remove('is-maxed');
      } else {
        this.elements.nudge.textContent = '\u2713 You\u2019ve unlocked the biggest discount';
        this.elements.nudge.classList.add('is-maxed');
      }
    }

    // Sticky "% off" pills
    document.querySelectorAll('[data-sticky-off]').forEach(el => {
      if (off > 0) {
        el.textContent = off + '% off';
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    });

    // Stepper bound states
    this.querySelectorAll('[data-qty-step]').forEach(b => {
      const d = parseInt(b.dataset.qtyStep, 10);
      b.disabled = (d < 0 && qty <= 1) || (d > 0 && qty >= this.maxQty);
    });
    this.querySelectorAll('[data-sticky-qty-step]').forEach(b => {
      const d = parseInt(b.dataset.stickyQtyStep, 10);
      b.disabled = (d < 0 && qty <= 1) || (d > 0 && qty >= this.maxQty);
    });
  }

  /* ============================================================
     SKIO BRIDGE
     ============================================================ */
  syncSkio() {
    if (!this.skio) return;
    try {
      if (this.state.variantId && this.skio.product && this.skio.product.variants) {
        const variant = this.skio.product.variants.find(v => v.id == this.state.variantId);
        if (variant) {
          this.skio.selectedVariant = variant;
          if (typeof this.skio.requestUpdate === 'function') {
            this.skio.requestUpdate();
          }
        }
      }

      if (this.state.option === 'subscribe' && this.state.sellingPlanId) {
        if (typeof this.skio.selectSellingPlan === 'function') {
          this.skio.selectSellingPlan(this.state.sellingPlanId);
        }
      } else if (this.state.option === 'onetime') {
        if (typeof this.skio.selectSellingPlanGroup === 'function') {
          this.skio.selectSellingPlanGroup(null);
        }
      }
    } catch (err) {
      console.warn('[lp-buybox] Skio sync error', err);
    }
  }

  /* ============================================================
     ATC - submit via fetch (cart-drawer.js intercepts and opens)
     ============================================================ */
  async addToCart() {
    if (!this.elements.atc) return;
    const selectedChip = this.querySelector('[data-variant-chip].is-selected');
    if (selectedChip && selectedChip.classList.contains('is-unavailable')) {
      return;
    }
    this.elements.atc.disabled = true;
    const labelEl = this.elements.atc.querySelector('.lp-buybox__atc-label');
    const originalText = labelEl ? labelEl.textContent : '';
    if (labelEl) labelEl.textContent = 'Adding';

    const restore = () => {
      this.elements.atc.disabled = false;
      if (labelEl) labelEl.textContent = originalText;
    };

    try {
      const payload = {
        items: [
          {
            id: this.state.variantId,
            quantity: this.state.qty
          }
        ]
      };

      if (this.state.option === 'subscribe' && this.state.sellingPlanId) {
        payload.items[0].selling_plan = this.state.sellingPlanId;
      }

      const cartAddUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
        ? window.Shopify.routes.root + 'cart/add.js'
        : '/cart/add.js';

      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        console.error('[lp-buybox] ATC failed', response.status, data);
        if (data && data.description) {
          alert(data.description);
        }
        restore();
        return;
      }

      document.dispatchEvent(new CustomEvent('lp-buybox:added-to-cart', { detail: data }));
      restore();
    } catch (err) {
      console.error('[lp-buybox] ATC exception', err);
      restore();
    }
  }
}

try {
  if (!customElements.get('lp-buybox')) {
    customElements.define('lp-buybox', LPBuybox);
  }
  if (document.body) {
    document.body.setAttribute('data-lp-buybox-loaded', 'true');
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      document.body.setAttribute('data-lp-buybox-loaded', 'true');
    });
  }
} catch (err) {
  console.error('[lp-buybox] FAILED TO REGISTER:', err);
}

/* ============================================================
   STICKY ATC OBSERVER
   ============================================================ */
document.addEventListener('DOMContentLoaded', () => {
  const buybox = document.querySelector('lp-buybox');
  const stickies = document.querySelectorAll('[data-lp-buybox-sticky]');
  if (!buybox || stickies.length === 0) return;

  stickies.forEach(s => {
    if (s.parentElement !== document.body) {
      document.body.appendChild(s);
    }
  });

  const showStickies = () => {
    stickies.forEach(s => {
      s.hidden = false;
      requestAnimationFrame(() => s.classList.add('is-visible'));
    });
  };
  const hideStickies = () => {
    stickies.forEach(s => {
      s.hidden = true;
      s.classList.remove('is-visible');
    });
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        hideStickies();
      } else if (entry.boundingClientRect.top < 0) {
        showStickies();
      } else {
        hideStickies();
      }
    });
  }, {
    rootMargin: '-100px 0px 0px 0px',
    threshold: 0
  });

  observer.observe(buybox);
});